import { SubCompositionLayer, VideoDescriptorLayer } from '../layers';
import { VideoDescriptor } from '../VideoDescriptor';

/**
 * Iterator that yields layers in render order, unrolling precomp layers in their order of appearance in the layers array.
 * Yielded values are tuples where the last element is the actual layer, and any preceding elements are SubCompositionLayers
 * representing the tree of compositions which the layer is nested in.
 *
 * This is useful because the standard `VideoDescriptor.layersIterator()` does not reflect render order, it just
 * iterates over each composition within the descriptor and yields the layers in those compositions.
 * This allows us to get a flattened view of all layers in the descriptor in the order they would be rendered,
 * which is useful for determining how layers stack on top of each other when rendered.
 */
export function* layerRenderOrderIterator(
  videoDescriptor: VideoDescriptor,
): IterableIterator<[...SubCompositionLayer[], VideoDescriptorLayer]> {
  const stack: [...SubCompositionLayer[], VideoDescriptorLayer][] = [
    ...videoDescriptor
      .getRootComposition()
      .layers.slice()
      .reverse()
      .map((layer) => [layer] as [...SubCompositionLayer[], VideoDescriptorLayer]),
  ];

  while (stack.length > 0) {
    const currentPath = stack.pop();
    if (!currentPath) {
      break;
    }

    const currentLayer = currentPath[currentPath.length - 1];
    if (currentLayer instanceof SubCompositionLayer) {
      for (const childLayer of currentLayer.composition.layers.slice().reverse()) {
        stack.push([...(currentPath as SubCompositionLayer[]), childLayer]);
      }
    } else {
      yield currentPath;
    }
  }
}
